import { useEffect, useRef } from "react";

export default function OrbCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let w = 0, h = 0, raf = 0;
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.max(1, w * dpr);
      canvas.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      mouse.ty = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    window.addEventListener("mousemove", onMouse);

    // Fibonacci sphere node lattice
    const N = 380;
    const phi = Math.PI * (3 - Math.sqrt(5));
    const nodes = [];
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const rad = Math.sqrt(Math.max(0, 1 - y * y));
      const th = phi * i;
      nodes.push([Math.cos(th) * rad, y, Math.sin(th) * rad]);
    }

    const slerp = (a, b, t) => {
      let dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
      dot = Math.min(1, Math.max(-1, dot));
      const om = Math.acos(dot);
      const so = Math.sin(om) || 1e-6;
      const k0 = Math.sin((1 - t) * om) / so;
      const k1 = Math.sin(t * om) / so;
      return [a[0] * k0 + b[0] * k1, a[1] * k0 + b[1] * k1, a[2] * k0 + b[2] * k1];
    };

    const arcs = [];
    for (let i = 0; i < 9; i++) {
      const a = nodes[Math.floor(Math.random() * N)];
      const b = nodes[Math.floor(Math.random() * N)];
      arcs.push({ a, b, speed: 0.0035 + Math.random() * 0.007, t: Math.random() });
    }

    const TILT = 0.42;
    let rot = 0.6;

    const project = (p, cx, cy, R) => {
      const cos = Math.cos(rot), sin = Math.sin(rot);
      const x = p[0] * cos - p[2] * sin;
      const z = p[0] * sin + p[2] * cos;
      const y = p[1];
      const ct = Math.cos(TILT + mouse.y * 0.12), st = Math.sin(TILT + mouse.y * 0.12);
      const y2 = y * ct - z * st;
      const z2 = y * st + z * ct;
      const s = 1.6 / (1.6 + z2 * 0.55);
      return { x: cx + x * R * s, y: cy + y2 * R * s, z: z2, s };
    };

    let t = 0;

    const render = () => {
      t += 1;
      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;
      rot += 0.0035 + mouse.x * 0.002;
      ctx.clearRect(0, 0, w, h);

      const R = Math.min(w, h) * 0.3;
      const cx = w * 0.5;
      const cy = h * 0.5 + Math.sin(t * 0.008) * 6;

      // ambient silver glow
      const glow = ctx.createRadialGradient(cx, cy, R * 0.3, cx, cy, R * 2.6);
      glow.addColorStop(0, "rgba(229,231,235,0.10)");
      glow.addColorStop(0.5, "rgba(156,163,175,0.04)");
      glow.addColorStop(1, "rgba(17,19,21,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);

      // faint structural rings
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 1.32, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, R * 1.62, 0, Math.PI * 2);
      ctx.stroke();

      // node lattice
      for (let i = 0; i < N; i++) {
        const pr = project(nodes[i], cx, cy, R);
        const front = (1 - pr.z) / 2;
        const alpha = 0.05 + front * 0.5;
        const size = 0.7 + front * 1.4;
        ctx.fillStyle =
          i % 8 === 0
            ? `rgba(255,255,255,${alpha})`
            : `rgba(156,163,175,${alpha})`;
        ctx.beginPath();
        ctx.arc(pr.x, pr.y, size, 0, Math.PI * 2);
        ctx.fill();
      }

      // data arcs + traveling pulses
      for (const arc of arcs) {
        arc.t = (arc.t + arc.speed) % 1;
        ctx.strokeStyle = "rgba(229,231,235,0.16)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        const steps = 26;
        for (let i = 0; i <= steps; i++) {
          const pr = project(slerp(arc.a, arc.b, i / steps), cx, cy, R);
          i === 0 ? ctx.moveTo(pr.x, pr.y) : ctx.lineTo(pr.x, pr.y);
        }
        ctx.stroke();

        for (const end of [arc.a, arc.b]) {
          const pr = project(end, cx, cy, R);
          ctx.fillStyle = "rgba(255,255,255,0.85)";
          ctx.beginPath();
          ctx.arc(pr.x, pr.y, 1.8, 0, Math.PI * 2);
          ctx.fill();
        }

        for (let k = 0; k < 4; k++) {
          const tt = arc.t - k * 0.02;
          if (tt < 0) continue;
          const pr = project(slerp(arc.a, arc.b, tt), cx, cy, R);
          const fade = 1 - k / 4;
          ctx.fillStyle = `rgba(255,255,255,${0.85 * fade})`;
          ctx.shadowColor = "#E5E7EB";
          ctx.shadowBlur = 14 * fade;
          ctx.beginPath();
          ctx.arc(pr.x, pr.y, 2.4 * fade + 0.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }
      }

      // rim light
      ctx.save();
      ctx.strokeStyle = "rgba(229,231,235,0.3)";
      ctx.lineWidth = 1.2;
      ctx.shadowColor = "rgba(229,231,235,0.5)";
      ctx.shadowBlur = 16;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 1.02, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      // bright core
      const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 0.35);
      core.addColorStop(0, "rgba(255,255,255,0.12)");
      core.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 0.35, 0, Math.PI * 2);
      ctx.fill();

      // ground reflection
      const gr = ctx.createRadialGradient(cx, cy + R * 1.7, 0, cx, cy + R * 1.7, R * 1.5);
      gr.addColorStop(0, "rgba(229,231,235,0.09)");
      gr.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = gr;
      ctx.beginPath();
      ctx.ellipse(cx, cy + R * 1.7, R * 1.5, R * 0.3, 0, 0, Math.PI * 2);
      ctx.fill();
    };

    const loop = () => {
      render();
      raf = requestAnimationFrame(loop);
    };

    if (reduce) {
      render();
    } else {
      loop();
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="hero-orb-canvas"
      className="h-full w-full"
      aria-hidden="true"
    />
  );
}
